import React, { useCallback, useEffect, useState } from 'react';
import { Star, Loader2, Save, User } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const api = {
  employees: () => apiClient.get('/hr/employees').then((r) => r.data),
  scorecard: (uid, y, q) => apiClient.get(`/hr/performance/scorecard?user_id=${uid}&year=${y}&quarter=${q}`).then((r) => r.data),
  saveReview: (p) => apiClient.post('/hr/performance/reviews', p).then((r) => r.data),
  listReviews: () => apiClient.get('/hr/performance/reviews').then((r) => r.data),
};

const PerformancePage = () => {
  const now = new Date();
  const [employees, setEmployees] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [scorecard, setScorecard] = useState(null);
  const [form, setForm] = useState({ overall_rating: 3, manager_comment: '', self_comment: '', status: 'draft', goals_next_quarter: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.employees().then((r) => setEmployees(r.data || [])); }, []);
  const load = useCallback(async () => {
    if (!selectedUid) return;
    const sc = await api.scorecard(selectedUid, year, quarter);
    setScorecard(sc);
    setForm({
      overall_rating: sc.review?.overall_rating || 3,
      manager_comment: sc.review?.manager_comment || '',
      self_comment: sc.review?.self_comment || '',
      status: sc.review?.status || 'draft',
      goals_next_quarter: sc.review?.goals_next_quarter || [],
    });
  }, [selectedUid, year, quarter]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try {
      await api.saveReview({ user_id: selectedUid, year, quarter, ...form,
        overall_rating: parseFloat(form.overall_rating) });
      toast({ title: 'Review saved' });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Performance</span>
        <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Quarterly reviews</h1>
        <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Scorecards auto-pull tasks & tickets from CRM. Add your rating and comments.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Employees */}
        <div className="lg:col-span-4 bg-white border border-black/5 rounded-2xl overflow-hidden">
          <div className="p-3 border-b border-black/5 text-sm font-medium">Employees</div>
          <div className="max-h-[70vh] overflow-y-auto">
            {employees.map((e) => (
              <button key={e.user_id} onClick={() => setSelectedUid(e.user_id)}
                data-testid={`perf-emp-${e.user_id}`}
                className={`w-full text-left p-3 border-b border-black/5 hover:bg-[#f7fbf8] ${selectedUid === e.user_id ? 'bg-[#f7fbf8]' : ''}`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold flex items-center justify-center">{(e.name || e.email || '?').charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{e.name || e.email}</div>
                    <div className="text-[11px] text-[#0b1a2e]/50 truncate">{e.designation || '—'}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
        {/* Scorecard */}
        <div className="lg:col-span-8">
          {!selectedUid ? (
            <div className="bg-white border border-black/5 rounded-2xl p-16 text-center text-[#0b1a2e]/50 text-sm">Select an employee to view scorecard.</div>
          ) : !scorecard ? <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d] inline" /></div> : (
            <div className="space-y-4">
              <div className="bg-white border border-black/5 rounded-2xl p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-[#0f5c3d]" />
                    <div className="text-lg font-semibold">Q{scorecard.quarter} · {scorecard.year}</div>
                    <div className="text-xs text-[#0b1a2e]/50">{scorecard.period}</div>
                  </div>
                  <div className="flex gap-1">
                    <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="w-20 px-2 py-1 border rounded text-sm" />
                    <select value={quarter} onChange={(e) => setQuarter(parseInt(e.target.value, 10))} data-testid="perf-quarter" className="px-2 py-1 border rounded text-sm bg-white">
                      {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  <Stat label="Tasks total" v={scorecard.stats.tasks_total} />
                  <Stat label="Tasks completed" v={scorecard.stats.tasks_completed} accent="emerald" />
                  <Stat label="Tasks overdue" v={scorecard.stats.tasks_overdue} accent="red" />
                  <Stat label="Avg task rating" v={scorecard.stats.avg_task_rating != null ? `${scorecard.stats.avg_task_rating}★` : '—'} />
                  <Stat label="Tickets total" v={scorecard.stats.tickets_total} />
                  <Stat label="Tickets resolved" v={scorecard.stats.tickets_resolved} accent="emerald" />
                  <Stat label="Avg ticket rating" v={scorecard.stats.avg_ticket_rating != null ? `${scorecard.stats.avg_ticket_rating}★` : '—'} />
                  <Stat label="Hours logged" v={scorecard.stats.hours_logged || 0} />
                </div>
              </div>
              <div className="bg-white border border-black/5 rounded-2xl p-5">
                <h3 className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-3">Review</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Overall rating (1-5)</label>
                    <div className="flex items-center gap-2 mt-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} onClick={() => setForm({ ...form, overall_rating: n })} data-testid={`rating-${n}`}
                          className={`p-2 ${n <= form.overall_rating ? 'text-amber-500' : 'text-slate-300'}`}>
                          <Star className="w-6 h-6 fill-current" />
                        </button>
                      ))}
                      <span className="ml-2 font-bold text-[#0f5c3d]">{form.overall_rating}/5</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Manager comment</label>
                    <textarea value={form.manager_comment} onChange={(e) => setForm({ ...form, manager_comment: e.target.value })}
                      data-testid="manager-comment" rows={4} className="w-full px-3 py-2 border rounded text-sm mt-1" placeholder="Strengths, achievements, areas to develop…" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Employee self-comment</label>
                    <textarea value={form.self_comment} onChange={(e) => setForm({ ...form, self_comment: e.target.value })} rows={3} className="w-full px-3 py-2 border rounded text-sm mt-1" placeholder="Achievements, learning, blockers…" />
                  </div>
                  <div className="flex items-center gap-3">
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-3 py-2 border rounded text-sm bg-white">
                      <option value="draft">Draft</option><option value="shared">Shared with employee</option><option value="acknowledged">Acknowledged</option>
                    </select>
                    <button onClick={save} disabled={saving} data-testid="save-review-btn"
                      className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">
                      {saving && <Loader2 className="w-3.5 h-3.5 inline animate-spin" />}<Save className="w-3.5 h-3.5" /> Save review
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, v, accent = 'default' }) => {
  const c = { default: 'text-[#0b1a2e]', emerald: 'text-emerald-700', red: 'text-red-600' }[accent];
  return (
    <div className="bg-[#f7fbf8] rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">{label}</div>
      <div className={`text-xl font-bold mt-1 ${c}`}>{v}</div>
    </div>
  );
};

export default PerformancePage;
